/**
 * MockExamPage — 受験中 (FR-10 / FR-16 / EF-01)
 * タイマー・前後ナビ・問題一覧マップ。focus_mode（ボトムバー非表示）。
 * 残り5分=琥珀警告、残り1分=赤警告。
 * 中断 → pauseMockExam + 残り時間保存。
 * 経験則 #15: ボタン disabled 中
 * 経験則 #63: タイマー色: 通常=accent, 残5分=amber, 残1分=red
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getMockExamQuestion, saveMockAnswer, pauseMockExam, submitMockExam, getActiveMockExam } from '../api'
import { ApiClientError } from '../api/client'
import type { MockExamQuestion } from '../api/types'
import Icon from '../components/Icon'
import { PageLoader } from '../components/Spinner'

const TIMER_WARNING_AMBER = 300 // 5分
const TIMER_WARNING_RED = 60 // 1分

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function timerColor(remaining: number): string {
  if (remaining <= TIMER_WARNING_RED) return 'var(--status-incorrect-color)'
  if (remaining <= TIMER_WARNING_AMBER) return 'var(--status-review-color)'
  return 'var(--accent)'
}

export default function MockExamPage() {
  const { sessionId, questionOrder } = useParams<{ sessionId: string; questionOrder: string }>()
  const navigate = useNavigate()

  const currentOrder = Number(questionOrder) || 1
  const initTotal = sessionId ? Number(sessionStorage.getItem(`mock_total_${sessionId}`) ?? 0) : 0
  const totalRef = useRef<number>(initTotal)

  const [question, setQuestion] = useState<MockExamQuestion | null>(null)
  const [answers, setAnswers] = useState<Map<number, number>>(new Map())
  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => {
    if (!sessionId) return 0
    const stored = sessionStorage.getItem(`mock_remaining_${sessionId}`)
    return stored ? Number(stored) : 0
  })
  const [sessionRestored, setSessionRestored] = useState(initTotal > 0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<number | null>(null)
  const remainingRef = useRef(remainingSeconds)

  useEffect(() => {
    remainingRef.current = remainingSeconds
  }, [remainingSeconds])

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const handleAutoSubmit = useCallback(async () => {
    if (!sessionId) return
    stopTimer()
    try {
      const result = await submitMockExam(sessionId)
      navigate(`/mock-exam/result/${result?.mock_exam_session_id ?? sessionId}`, { state: { result } })
    } catch {
      navigate('/mock-exam')
    }
  }, [sessionId, stopTimer, navigate])

  // IMPL-09 fix: sessionStorage が空（直接URL）の場合、getActiveMockExam で復元
  useEffect(() => {
    if (!sessionId) return
    if (sessionRestored) return
    getActiveMockExam()
      .then((session) => {
        if (session && session.id === sessionId) {
          const total = session.total_questions ?? 0
          const remaining = session.remaining_seconds ?? 0
          if (total > 0) {
            totalRef.current = total
            sessionStorage.setItem(`mock_total_${sessionId}`, String(total))
          }
          if (remaining > 0 && remainingSeconds <= 0) {
            setRemainingSeconds(remaining)
            sessionStorage.setItem(`mock_remaining_${sessionId}`, String(remaining))
          }
          setSessionRestored(true)
        } else {
          setError('模擬試験セッションの情報を復元できませんでした。模擬試験トップから再開してください。')
          setSessionRestored(true)
        }
      })
      .catch(() => {
        setError('模擬試験セッションの情報を復元できませんでした。模擬試験トップから再開してください。')
        setSessionRestored(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sessionRestored])

  // 問題取得
  useEffect(() => {
    if (!sessionId) return
    setLoading(true)
    setError(null)
    getMockExamQuestion(sessionId, currentOrder)
      .then((q) => {
        setQuestion(q)
        if (q != null && q.user_answer != null) {
          setAnswers((prev) => new Map(prev).set(currentOrder, q.user_answer!))
        }
      })
      .catch((err) => {
        if (err instanceof ApiClientError) setError(err.message)
        else setError('問題の取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }, [sessionId, currentOrder])

  // タイマー起動
  const timerStartedRef = useRef(false)
  useEffect(() => {
    if (timerStartedRef.current) return
    if (remainingSeconds <= 0) return
    timerStartedRef.current = true
    timerRef.current = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1
        if (next <= 0) {
          stopTimer()
          handleAutoSubmit()
          return 0
        }
        return next
      })
    }, 1000)
    return stopTimer
  }, [remainingSeconds, stopTimer, handleAutoSubmit])

  // アンマウント時に残り時間を保存
  useEffect(() => {
    return () => {
      if (remainingRef.current > 0 && sessionId) {
        sessionStorage.setItem(`mock_remaining_${sessionId}`, String(remainingRef.current))
      }
    }
  }, [sessionId])

  const handleSelect = async (choiceNo: number) => {
    if (!sessionId || submitting) return
    const prev = answers.get(currentOrder)
    setAnswers((m) => new Map(m).set(currentOrder, choiceNo))
    saveMockAnswer(sessionId, {
      question_order: currentOrder,
      user_answer: choiceNo,
      answered_at: new Date().toISOString(),
    }).catch(() => {
      setAnswers((m) => {
        const n = new Map(m)
        if (prev == null) n.delete(currentOrder)
        else n.set(currentOrder, prev)
        return n
      })
    })
  }

  const handlePrev = () => {
    if (currentOrder <= 1) return
    navigate(`/mock-exam/session/${sessionId}/${currentOrder - 1}`)
  }

  const handleNext = () => {
    if (currentOrder >= totalRef.current) return
    navigate(`/mock-exam/session/${sessionId}/${currentOrder + 1}`)
  }

  const handlePause = async () => {
    if (!sessionId) return
    stopTimer()
    setPausing(true)
    try {
      await pauseMockExam(sessionId, { remaining_seconds: remainingRef.current })
      sessionStorage.setItem(`mock_remaining_${sessionId}`, String(remainingRef.current))
      navigate('/mock-exam')
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('中断に失敗しました')
      // タイマー再開
      timerRef.current = window.setInterval(() => {
        setRemainingSeconds((prev) => {
          const next = prev - 1
          if (next <= 0) {
            stopTimer()
            handleAutoSubmit()
            return 0
          }
          return next
        })
      }, 1000)
    } finally {
      setPausing(false)
    }
  }

  const handleSubmit = async () => {
    if (!sessionId) return
    if (!window.confirm('すべての問題に解答しましたか？解答を確定して採点します。')) return
    stopTimer()
    setSubmitting(true)
    try {
      const result = await submitMockExam(sessionId)
      sessionStorage.removeItem(`mock_remaining_${sessionId}`)
      navigate(`/mock-exam/result/${result?.mock_exam_session_id ?? sessionId}`, { state: { result } })
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('提出に失敗しました')
      setSubmitting(false)
    }
  }

  const answeredCount = answers.size
  const color = timerColor(remainingSeconds)

  return (
    <div className="mx-auto max-w-content p-screen">
      {/* ヘッダー */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={handlePause}
          disabled={pausing || submitting}
          className="flex items-center gap-1 text-[13px] text-text-secondary transition-colors hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon name="x-circle" size={16} />
          {pausing ? '中断中...' : '中断'}
        </button>

        {/* タイマー（色は残時間で動的に切替 #63） */}
        <div
          data-testid="mock-timer"
          className="flex items-center gap-1 font-numeric text-lg font-bold tabular-nums"
          style={{ color }}
        >
          <Icon name="clock" size={16} />
          残り {formatTime(remainingSeconds)}
        </div>

        {/* 進捗 */}
        <div data-testid="mock-progress" className="text-[13px] font-semibold text-text-secondary">
          問 {currentOrder} / {totalRef.current || '—'}
        </div>
      </div>

      {error && (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : question ? (
        <>
          {/* 問題文 */}
          <div data-testid="mock-question" className="mb-5 text-base font-medium leading-body text-text-primary">
            {question.question_text}
          </div>

          {/* 選択肢 */}
          <div data-testid="choice-list" className="mb-5 flex flex-col gap-2">
            {question.choices?.map((c) => {
              const isSelected = answers.get(currentOrder) === c.choice_no
              return (
                <button
                  key={c.choice_no}
                  data-testid="choice-option"
                  onClick={() => handleSelect(c.choice_no ?? 0)}
                  disabled={submitting}
                  aria-pressed={isSelected}
                  className={[
                    'flex min-h-[52px] w-full items-center gap-3 rounded-md px-3.5 py-3 text-left text-[15px] text-text-primary transition-colors duration-fast',
                    isSelected ? 'border-2 border-accent bg-accent-subtle' : 'border border-border-strong bg-surface',
                    submitting ? 'cursor-not-allowed' : 'cursor-pointer',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'flex h-6 w-6 flex-none items-center justify-center rounded text-[13px] font-bold',
                      isSelected ? 'bg-accent text-white' : 'bg-surface-alt text-text-secondary',
                    ].join(' ')}
                  >
                    {c.choice_no}
                  </span>
                  <span className="flex-1">{c.choice_text}</span>
                </button>
              )
            })}
          </div>

          {/* 前後ナビ */}
          <div className="mb-4 flex gap-2">
            <button
              data-testid="mock-prev"
              onClick={handlePrev}
              disabled={currentOrder <= 1 || submitting}
              className="flex h-11 flex-1 items-center justify-center gap-1 rounded-md border border-border-strong bg-surface text-sm font-semibold text-text-primary transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:bg-surface"
            >
              <Icon name="chevron-left" size={16} />
              前の問題
            </button>
            <button
              data-testid="mock-next"
              onClick={handleNext}
              disabled={(totalRef.current > 0 && currentOrder >= totalRef.current) || submitting}
              className="flex h-11 flex-1 items-center justify-center gap-1 rounded-md border border-border-strong bg-surface text-sm font-semibold text-text-primary transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:bg-surface"
            >
              次の問題
              <Icon name="chevron-right" size={16} />
            </button>
          </div>

          {/* 問題一覧マップ */}
          {totalRef.current > 0 && (
            <div data-testid="mock-question-map" className="card mb-4 px-3.5 py-3">
              <div className="mb-2 text-xs text-text-secondary">
                問題一覧 — 解答済み {answeredCount} / {totalRef.current}問
              </div>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: totalRef.current }, (_, i) => {
                  const order = i + 1
                  const answered = answers.has(order)
                  const isCurrent = order === currentOrder
                  return (
                    <button
                      key={order}
                      onClick={() => navigate(`/mock-exam/session/${sessionId}/${order}`)}
                      className={[
                        'h-7 w-7 rounded text-[11px] font-semibold transition-colors',
                        isCurrent ? 'border-2 border-accent' : 'border border-border',
                        answered
                          ? 'bg-accent text-white'
                          : isCurrent
                            ? 'bg-accent-subtle text-accent'
                            : 'bg-surface-alt text-text-secondary',
                      ].join(' ')}
                    >
                      {order}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* 提出ボタン */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex h-12 w-full items-center justify-center rounded-md bg-accent text-base font-semibold text-text-onaccent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-text-muted disabled:opacity-50"
          >
            {submitting ? '採点中...' : '解答を確定して採点する'}
          </button>
        </>
      ) : null}
    </div>
  )
}
